import { Minus, Plus } from "lucide-react";

/** Premium stepper — Fit kartı DNA'sında:
 * - `p-4` padding, iki satır text (label + hint), tam genişlik satır.
 * - Sağda inline mini stepper: [- 00 +] — rakam ortada tabular-nums, 20px.
 * - Yükseklik Fit kartı ile birebir aynı — modal CLS'siz. */
export const StepperField = ({ label, unit, hint, min, max, step: stepBy, value, onChange, testId, delayMs = 0, requiredBadge = false }) => {
    const clamp = (n) => Math.max(min, Math.min(max, n));
    const inc = () => onChange(clamp((value ?? min) + stepBy));
    const dec = () => onChange(clamp((value ?? max) - stepBy));
    return (
        <div
            data-slot="stepper-field"
            className="w-full h-20 flex items-center justify-between gap-4 rounded-lg border border-border bg-background px-4 py-3 transition-all sayz-fms-chip hover:border-foreground/30 hover:-translate-y-[1px] hover:shadow-[0_2px_10px_-2px_rgba(0,0,0,0.06)] focus-within:border-foreground/50"
            style={{ animationDelay: `${delayMs}ms` }}
        >
            {/* Sol: Fit kartındaki hiyerarşinin birebir aynısı — başlık + mikro açıklama */}
            <div className="min-w-0">
                <div className="text-[15px] font-semibold text-foreground leading-tight flex items-center gap-1.5">
                    <span>{label}</span>
                    {requiredBadge && (
                        <span
                            className="inline-flex items-center h-[16px] px-1.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-foreground/10 text-foreground/80"
                            data-testid={`${testId}-required-badge`}
                        >
                            Zorunlu
                        </span>
                    )}
                </div>
                <div className="text-[12px] text-muted-foreground mt-0.5 leading-snug">{hint}</div>
            </div>
            {/* Sağ: inline stepper + unit sinyali (unit, radio-check boyutlarında minimal) */}
            <div className="flex items-center gap-1 shrink-0">
                <button
                    type="button"
                    onClick={dec}
                    className="h-7 w-7 shrink-0 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                    aria-label={`${label} azalt`}
                >
                    <Minus className="h-4 w-4" strokeWidth={2} />
                </button>
                <input
                    type="number"
                    inputMode="numeric"
                    min={min}
                    max={max}
                    value={value ?? ""}
                    onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") { onChange(undefined); return; }
                        const n = Number(raw);
                        if (Number.isFinite(n)) onChange(n);
                    }}
                    className="w-12 text-center text-[20px] font-semibold tabular-nums bg-transparent border-0 outline-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="—"
                    data-testid={testId}
                />
                <button
                    type="button"
                    onClick={inc}
                    className="h-7 w-7 shrink-0 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                    aria-label={`${label} arttır`}
                >
                    <Plus className="h-4 w-4" strokeWidth={2} />
                </button>
                {/* Unit — minimal typographic suffix, non-interactive */}
                <span className="ml-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 w-6 text-left">
                    {unit}
                </span>
            </div>
        </div>
    );
};
